import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Loader2,
  NotebookPen,
  Star,
  Stethoscope,
  UserRound,
  Wallet,
  XCircle,
} from 'lucide-react';
import { appointmentService, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { parseApiDateTime } from '@/lib/dateTime';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

const STATUS_BY_ID = {
  1: 'pending',
  2: 'accepted',
  3: 'in_clinic',
  4: 'done',
  5: 'cancel',
  6: 'cancel',
  7: 'pending',
};

const NEXT_STATUS_BY_CURRENT = {
  pending: 'accepted',
  accepted: 'in_clinic',
  in_clinic: 'done',
};

const normalizeAppointmentState = (state, statusId) => {
  const raw = String(state || STATUS_BY_ID[statusId] || 'pending').trim().toLowerCase();

  if (['accepted', 'confirmed', 'مقبول'].includes(raw)) return 'accepted';
  if (['pending', 'draft', 'request', 'waiting', 'قيد الانتظار'].includes(raw)) return 'pending';
  if (['in_clinic', 'in_progress', 'داخل العيادة'].includes(raw)) return 'in_clinic';
  if (['done', 'completed', 'منتهي', 'مكتمل'].includes(raw)) return 'done';
  if (['cancel', 'cancelled', 'canceled', 'rejected', 'no_show', 'ملغي', 'ملغى'].includes(raw)) return 'cancel';
  if (['unfinished', 'غير مكتمل'].includes(raw)) return 'unfinished';

  return raw;
};

const getStatusPresentation = (state, lang) => {
  const normalized = normalizeAppointmentState(state);
  const map = {
    pending: {
      label: lang === 'ar' ? 'قيد الانتظار' : 'Pending',
      badge: 'bg-amber-500/12 text-amber-200 border border-amber-400/20',
      tint: 'text-amber-200',
    },
    accepted: {
      label: lang === 'ar' ? 'مقبول' : 'Accepted',
      badge: 'bg-emerald-500/12 text-emerald-200 border border-emerald-400/20',
      tint: 'text-emerald-200',
    },
    in_clinic: {
      label: lang === 'ar' ? 'داخل العيادة' : 'In The Clinic',
      badge: 'bg-fuchsia-500/12 text-fuchsia-200 border border-fuchsia-400/20',
      tint: 'text-fuchsia-200',
    },
    done: {
      label: lang === 'ar' ? 'منتهي' : 'Done',
      badge: 'bg-sky-500/12 text-sky-200 border border-sky-400/20',
      tint: 'text-sky-200',
    },
    cancel: {
      label: lang === 'ar' ? 'ملغي' : 'Cancelled',
      badge: 'bg-rose-500/12 text-rose-200 border border-rose-400/20',
      tint: 'text-rose-200',
    },
    unfinished: {
      label: lang === 'ar' ? 'غير مكتمل' : 'Unfinished',
      badge: 'bg-orange-500/12 text-orange-200 border border-orange-400/20',
      tint: 'text-orange-200',
    },
  };

  return map[normalized] || map.pending;
};

const getAppointmentTypeLabel = (appointment, lang) => {
  if (appointment.service_name) return appointment.service_name;

  const type = String(appointment.appointment_type || '').trim().toLowerCase();
  const map = {
    regular: lang === 'ar' ? 'ضمن العيادة' : 'At Clinic',
    clinic: lang === 'ar' ? 'ضمن العيادة' : 'At Clinic',
    online: lang === 'ar' ? 'استشارة أونلاين' : 'Online Consultation',
    waiting: lang === 'ar' ? 'موعد انتظار' : 'Waiting Appointment',
    emergency: lang === 'ar' ? 'إسعافي' : 'Emergency',
    address: lang === 'ar' ? 'زيارة منزلية' : 'Home Visit',
  };

  return map[type] || appointment.appointment_type || (lang === 'ar' ? 'موعد' : 'Appointment');
};

const getPaymentStatusLabel = (status, lang) => {
  const normalized = String(status || '').trim().toLowerCase();
  const map = {
    paid: lang === 'ar' ? 'مدفوع' : 'Paid',
    unpaid: lang === 'ar' ? 'غير مدفوع' : 'Unpaid',
    refunded: lang === 'ar' ? 'مسترجع' : 'Refunded',
  };
  return map[normalized] || status || '-';
};

const canCancelAppointment = (state) => ['pending', 'accepted'].includes(normalizeAppointmentState(state));

const getNextStatus = (state) => NEXT_STATUS_BY_CURRENT[normalizeAppointmentState(state)] || null;

const readBoolean = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', ''].includes(normalized)) return false;
  return fallback;
};

const readNumber = (value) => {
  if (value == null || value === false || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readText = (value) => {
  if (value == null || value === false) return '';
  return String(value).trim();
};

const readObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const parseAppointmentDate = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const parsed = parseApiDateTime(value);
    if (parsed) {
      return parsed;
    }
  }
  return null;
};

const transformAppointment = (apt, lang) => {
  const state = normalizeAppointmentState(apt.state, apt.appointment_status_id);
  const doctorReview = readObject(
    apt.doctor_review ||
    apt.doctorReview ||
    apt.review ||
    apt.doctor_review_data ||
    apt.review_data
  );
  const doctorReviewRows = Array.isArray(apt.doctor_reviews)
    ? apt.doctor_reviews
    : Array.isArray(apt.reviews)
      ? apt.reviews
      : [];
  const hasDoctorReviewPayload =
    Object.keys(doctorReview).length > 0 ||
    doctorReviewRows.length > 0 ||
    readNumber(apt.doctor_review_id) != null ||
    readNumber(apt.review_id) != null;
  const canReviewDoctor = readBoolean(apt.can_review_doctor, true);
  const doctorReviewedFlag = readBoolean(
    apt.doctor_reviewed ??
    apt.doctor_review_submitted ??
    apt.is_doctor_reviewed ??
    apt.has_doctor_review ??
    apt.has_review ??
    apt.reviewed ??
    apt.already_reviewed,
    hasDoctorReviewPayload
  );
  const doctorReviewRating =
    readNumber(apt.doctor_review_rating) ??
    readNumber(apt.review_rating) ??
    readNumber(doctorReview.rating) ??
    readNumber(doctorReview.rate);
  const doctorReviewComment =
    readText(apt.doctor_review_comment) ||
    readText(apt.review_comment) ||
    readText(doctorReview.review) ||
    readText(doctorReview.comment) ||
    readText(doctorReview.text);
  const patientNameParts = [
    getLocalizedValue(apt.patient?.first_name, lang),
    getLocalizedValue(apt.patient?.last_name, lang),
  ].filter(Boolean);

  return {
    ...apt,
    id: apt.id,
    reference: apt.reference || apt.name,
    doctor: {
      id: apt.doctor?.id || apt.doctor_id,
      name: getLocalizedValue(apt.doctor?.name, lang) || apt.doctor_name,
      image: normalizeImageValue(apt.doctor?.image || apt.doctor_image || apt.doctor?.image_url || apt.doctor?.media?.[0]?.url || apt.doctor?.media?.[0]?.thumb || apt.doctor?.media?.[0]?.icon || ''),
      gender: apt.doctor?.gender || apt.doctor?.sex || '',
      specialty: apt.doctor?.specialties?.[0]
        ? {
            name: getLocalizedValue(apt.doctor.specialties[0].name, lang),
          }
        : null,
    },
    patient: {
      id: apt.patient?.id || apt.patient_id,
      name:
        patientNameParts.join(' ').trim() ||
        getLocalizedValue(apt.patient?.name, lang) ||
        apt.patient_name ||
        '',
    },
    clinic: {
      name: getLocalizedValue(apt.clinic?.name, lang) || apt.clinic_name || '',
    },
    appointment_at: apt.appointment_at || apt.appointment_date || apt.start_at,
    start_at: apt.start_at || apt.appointment_at || apt.appointment_date,
    ends_at: apt.ends_at || apt.end_datetime,
    duration: apt.duration || apt.session_duration || 30,
    appointment_type: apt.appointment_type || apt.type || 'regular',
    service_name: getLocalizedValue(apt.doctor_service?.name, lang) || apt.service_name,
    state,
    notes: apt.notes || '',
    payment_status: apt.payment_status || '',
    price: apt.price ?? apt.total,
    currency: apt.currency || 'USD',
    queue_display_label: apt.queue_display_label || '',
    remaining_turns_count: apt.remaining_turns_count ?? apt.remaining_queue_turns,
    estimated_wait_time: apt.estimated_wait_time ?? apt.estimated_turn_arrival_minutes,
    show_queue_number_to_patient: !!apt.show_queue_number_to_patient,
    show_remaining_turns_to_patient: !!apt.show_remaining_turns_to_patient,
    show_estimated_turn_time_to_patient: !!apt.show_estimated_turn_time_to_patient,
    doctor_reviewed: doctorReviewedFlag || hasDoctorReviewPayload || canReviewDoctor === false,
    doctor_review_id: readNumber(apt.doctor_review_id) ?? readNumber(apt.review_id) ?? readNumber(doctorReview.id),
    doctor_review_rating: doctorReviewRating,
    doctor_review_comment: doctorReviewComment,
    can_review_doctor: canReviewDoctor,
  };
};

const Section = ({ icon: Icon, title, children }) => (
  <Card className="theme-panel">
    <CardContent className="p-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="rounded-2xl bg-slate-100 p-3 text-slate-700 dark:bg-white/8 dark:text-slate-100">
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </CardContent>
  </Card>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-6 border-b border-slate-200 pb-3 last:border-b-0 last:pb-0 dark:border-white/8">
    <span className="text-base font-semibold text-slate-500 dark:text-slate-400">{label}</span>
    <span className="max-w-[60%] text-right text-base font-bold text-slate-900 dark:text-white">{value || '-'}</span>
  </div>
);

const SchedulePointCard = ({ accentClass, label, time, dateTime, align = 'start' }) => (
  <div className={`relative overflow-hidden rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04] ${align === 'end' ? 'text-right' : 'text-left'}`}>
    <div className={`absolute inset-x-0 top-0 h-1.5 ${accentClass}`} />
    <div className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-black tracking-[0.18em] text-slate-600 dark:bg-white/10 dark:text-slate-300">
      {label}
    </div>
    <div className="mt-4 text-3xl font-black text-slate-900 dark:text-white">{time || '-'}</div>
    <div className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">{dateTime || '-'}</div>
  </div>
);

const headerBadgeClass =
  'rounded-full border border-slate-400/20 bg-slate-900/80 px-4 py-2 text-sm font-bold text-white shadow-sm dark:border-white/10 dark:bg-white/10 dark:text-white';

const AppointmentDetailsPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language || 'ar';
  const locale = lang === 'ar' ? ar : enUS;
  const backPath =
    location.state?.from ||
    (location.pathname.startsWith('/doctor/') ? '/doctor/appointments' : '/appointments');
  const [appointment, setAppointment] = useState(() =>
    location.state?.appointment ? transformAppointment(location.state.appointment, lang) : null
  );
  const [loading, setLoading] = useState(!location.state?.appointment);
  const [cancelling, setCancelling] = useState(false);
  const [advancingStatus, setAdvancingStatus] = useState(false);
  const [realtimeRefreshKey, setRealtimeRefreshKey] = useState(0);
  const isCancelInProgress = useRef(false);
  const isStatusInProgress = useRef(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let active = true;
    const stateAppointment = location.state?.appointment;

    const fetchAppointment = async () => {
      if (!id) {
        if (active) setLoading(false);
        return;
      }

      setLoading(!stateAppointment);
      try {
        const response = await appointmentService.getById(id);
        const data = response?.data?.data || {};
        const rawAppointment = data.appointment || data;
        if (active) {
          setAppointment(transformAppointment(rawAppointment, lang));
        }
      } catch (error) {
        console.error('Failed to fetch appointment details:', error);
        if (active && !stateAppointment) {
          toast.error(lang === 'ar' ? 'تعذر تحميل تفاصيل الموعد' : 'Failed to load appointment details');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (stateAppointment) {
      setAppointment(transformAppointment(stateAppointment, lang));
    }

    fetchAppointment();

    return () => {
      active = false;
    };
  }, [id, lang, location.state?.appointment, realtimeRefreshKey]);

  useEffect(() => {
    const refresh = (event) => {
      if (!event.detail?.appointment_id || String(event.detail.appointment_id) === String(id)) {
        setRealtimeRefreshKey((value) => value + 1);
      }
    };
    window.addEventListener('dactrha:appointment-changed', refresh);
    return () => window.removeEventListener('dactrha:appointment-changed', refresh);
  }, [id]);

  const endAt = useMemo(() => {
    const start = parseAppointmentDate(
      appointment?.appointment_at,
      appointment?.start_at,
      appointment?.appointment_date,
    );
    if (!start) return null;
    const explicitEnd = parseAppointmentDate(appointment?.ends_at);
    if (explicitEnd) return explicitEnd;
    return new Date(start.getTime() + (appointment.duration || 30) * 60000);
  }, [appointment]);

  const handleCancel = async () => {
    if (!appointment || isCancelInProgress.current) return;

    isCancelInProgress.current = true;
    setCancelling(true);

    try {
      const response = await appointmentService.cancel(appointment.id);
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to cancel');
      }

      const updated = {
        ...appointment,
        state: 'cancel',
      };

      setAppointment(updated);
      toast.success(lang === 'ar' ? 'تم إلغاء الموعد بنجاح' : 'Appointment cancelled successfully');
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      toast.error(lang === 'ar' ? message || 'فشل في إلغاء الموعد' : message || 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
      setTimeout(() => {
        isCancelInProgress.current = false;
      }, 1500);
    }
  };

  const handleAdvanceStatus = async () => {
    if (!appointment || isStatusInProgress.current) return;

    const nextStatus = getNextStatus(appointment.state);
    if (!nextStatus) return;

    isStatusInProgress.current = true;
    setAdvancingStatus(true);

    try {
      await appointmentService.updateStatus(appointment.id, nextStatus);

      setAppointment((current) =>
        current
          ? {
              ...current,
              state: nextStatus,
            }
          : current
      );

      toast.success(
        lang === 'ar'
          ? `تم تحديث حالة الموعد إلى ${getStatusPresentation(nextStatus, 'ar').label}`
          : `Appointment status updated to ${getStatusPresentation(nextStatus, 'en').label}`
      );
    } catch (error) {
      const message = error?.response?.data?.message || error?.message;
      toast.error(
        lang === 'ar'
          ? message || 'فشل في تحديث حالة الموعد'
          : message || 'Failed to update appointment status'
      );
    } finally {
      setAdvancingStatus(false);
      setTimeout(() => {
        isStatusInProgress.current = false;
      }, 1500);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="theme-shell min-h-screen">
        <div className="container mx-auto px-4 py-10">
          <div className="flex items-center gap-3 text-slate-900 dark:text-white">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{lang === 'ar' ? 'جاري تحميل التفاصيل...' : 'Loading details...'}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="theme-shell min-h-screen">
        <div className="container mx-auto px-4 py-10">
          <Card className="theme-panel">
            <CardContent className="p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-slate-500 dark:text-slate-300" />
              <h1 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">
                {lang === 'ar' ? 'تعذر العثور على الموعد' : 'Appointment not found'}
              </h1>
              <Button className="mt-6 bg-[#2f6fed] hover:bg-[#255fd0]" asChild>
                <Link to="/appointments">{lang === 'ar' ? 'العودة إلى المواعيد' : 'Back to appointments'}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusPresentation = getStatusPresentation(appointment.state, lang);
  const appointmentTypeLabel = getAppointmentTypeLabel(appointment, lang);
  const appointmentStart = parseAppointmentDate(
    appointment.appointment_at,
    appointment.start_at,
    appointment.appointment_date,
  );
  const formattedDateLong = appointmentStart
    ? format(appointmentStart, 'EEEE, MMMM d, yyyy', { locale })
    : '-';
  const formattedDateShort = appointmentStart
    ? format(appointmentStart, 'dd MMM yyyy', { locale })
    : '-';
  const formattedStartTime = appointmentStart
    ? format(appointmentStart, 'hh:mm a', { locale })
    : '-';
  const formattedStartDateTime = appointmentStart
    ? format(appointmentStart, 'dd MMM yyyy - hh:mm a', { locale })
    : '-';
  const formattedEndTime = endAt ? format(endAt, 'hh:mm a', { locale }) : '-';
  const formattedEndDateTime = endAt ? format(endAt, 'dd MMM yyyy - hh:mm a', { locale }) : '-';
  const canCancel = canCancelAppointment(appointment.state);
  const isDoctorView = user?.user_type === 'doctor' || location.pathname.startsWith('/doctor/');
  const nextStatus = isDoctorView ? getNextStatus(appointment.state) : null;
  const nextStatusLabel = nextStatus ? getStatusPresentation(nextStatus, lang).label : null;
  const isCompleted = normalizeAppointmentState(appointment.state) === 'done';
  const hasDoctorReview = !!appointment.doctor_reviewed;
  const canLeaveDoctorReview =
    !isDoctorView &&
    isCompleted &&
    !hasDoctorReview &&
    appointment.can_review_doctor !== false &&
    !!(appointment.id || id) &&
    !!appointment.doctor?.id;
  const reviewPath = appointment.doctor?.id
    ? `/rate/${appointment.doctor.id}?appointment=${appointment.id || id}`
    : '';

  return (
    <div className="theme-shell min-h-screen">
      <section className="theme-hero">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6 flex items-center gap-3">
            <Button
              variant="ghost"
              className="theme-outline-button h-11 rounded-xl px-4"
              onClick={() => navigate(backPath)}
            >
              <ArrowLeft className="me-2 h-4 w-4" />
              {lang === 'ar' ? 'رجوع' : 'Back'}
            </Button>
          </div>

          <Card className="theme-panel">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-gradient-to-br from-[#7488f2]/30 to-slate-100 text-slate-700 dark:to-white/5 dark:text-slate-100">
                  <UserRound className="h-11 w-11" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h1 className="text-3xl font-black text-slate-900 dark:text-white">
                        {appointment.doctor?.name || (lang === 'ar' ? 'الطبيب' : 'Doctor')}
                      </h1>
                      <p className="mt-1 text-lg text-slate-600 dark:text-slate-300">
                        {appointment.doctor?.specialty?.name || (lang === 'ar' ? 'طبيب' : 'Doctor')}
                      </p>
                      <p className="mt-3 text-2xl font-extrabold text-slate-900 dark:text-white">{formattedDateLong}</p>
                    </div>

                    <div className={`inline-flex w-fit rounded-full px-4 py-1.5 text-sm font-bold ${statusPresentation.badge}`}>
                      {statusPresentation.label}
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <div className={headerBadgeClass}>
                      {appointmentTypeLabel}
                    </div>
                    {appointment.reference && (
                      <div className={headerBadgeClass}>
                        {appointment.reference}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="container mx-auto space-y-6 px-4 py-8">
        <Section icon={ClipboardList} title={lang === 'ar' ? 'معلومات الموعد' : 'Appointment Information'}>
          {appointment.reference && (
            <DetailRow label={lang === 'ar' ? 'رقم الموعد' : 'Appointment ID'} value={appointment.reference} />
          )}
          <DetailRow label={lang === 'ar' ? 'الحالة' : 'Status'} value={statusPresentation.label} />
          <DetailRow label={lang === 'ar' ? 'النوع' : 'Type'} value={appointmentTypeLabel} />
          {appointment.service_name && (
            <DetailRow label={lang === 'ar' ? 'الخدمة' : 'Service'} value={appointment.service_name} />
          )}
          <DetailRow label={lang === 'ar' ? 'وقت البدء' : 'Start Time'} value={formattedStartTime} />
          <DetailRow label={lang === 'ar' ? 'وقت الانتهاء' : 'End Time'} value={formattedEndTime} />
          <DetailRow
            label={lang === 'ar' ? 'المدة' : 'Duration'}
            value={`${appointment.duration} ${lang === 'ar' ? 'دقيقة' : 'Minutes'}`}
          />
          {appointment.show_queue_number_to_patient && appointment.queue_display_label && (
            <DetailRow
              label={lang === 'ar' ? 'رقم الدور' : 'Queue Number'}
              value={appointment.queue_display_label}
            />
          )}
          {appointment.show_remaining_turns_to_patient && appointment.remaining_turns_count != null && (
            <DetailRow
              label={lang === 'ar' ? 'الأدوار المتبقية' : 'Remaining Turns'}
              value={String(appointment.remaining_turns_count)}
            />
          )}
          {appointment.show_estimated_turn_time_to_patient && appointment.estimated_wait_time != null && (
            <DetailRow
              label={lang === 'ar' ? 'الوقت المتوقع للدور' : 'Estimated Turn Time'}
              value={`${appointment.estimated_wait_time} ${lang === 'ar' ? 'دقيقة' : 'Minutes'}`}
            />
          )}
          {appointment.patient?.id && (
            <DetailRow label={lang === 'ar' ? 'رقم المريض' : 'Patient ID'} value={String(appointment.patient.id)} />
          )}
          {appointment.patient?.name && (
            <DetailRow label={lang === 'ar' ? 'اسم المريض' : 'Patient Name'} value={appointment.patient.name} />
          )}
        </Section>

        <Section icon={Calendar} title={lang === 'ar' ? 'جدولة الموعد' : 'Appointment Schedule'}>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center">
            <SchedulePointCard
              accentClass="bg-gradient-to-r from-emerald-500 to-teal-500"
              label={lang === 'ar' ? 'البداية' : 'START'}
              time={formattedStartTime}
              dateTime={formattedStartDateTime}
              align={lang === 'ar' ? 'end' : 'start'}
            />
            <div className="flex items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300">
                <ArrowRight className={`h-5 w-5 ${lang === 'ar' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <SchedulePointCard
              accentClass="bg-gradient-to-r from-rose-500 to-orange-500"
              label={lang === 'ar' ? 'النهاية' : 'END'}
              time={formattedEndTime}
              dateTime={formattedEndDateTime}
              align={lang === 'ar' ? 'end' : 'start'}
            />
          </div>
          <DetailRow label={lang === 'ar' ? 'التاريخ' : 'Date'} value={formattedDateShort} />
          <DetailRow label={lang === 'ar' ? 'يبدأ في' : 'Starts At'} value={formattedStartDateTime} />
          <DetailRow label={lang === 'ar' ? 'ينتهي في' : 'Ends At'} value={formattedEndDateTime} />
        </Section>

        <Section icon={Wallet} title={lang === 'ar' ? 'التسعير' : 'Pricing'}>
          <DetailRow
            label={lang === 'ar' ? 'حالة الدفع' : 'Payment Status'}
            value={getPaymentStatusLabel(appointment.payment_status, lang)}
          />
          {appointment.price != null && (
            <DetailRow
              label={lang === 'ar' ? 'سعر الاستشارة' : 'Consultation Price'}
              value={`${appointment.price} ${appointment.currency || ''}`.trim()}
            />
          )}
          <DetailRow label={lang === 'ar' ? 'الضريبة' : 'Tax'} value="-" />
          {appointment.price != null && (
            <DetailRow
              label={lang === 'ar' ? 'الإجمالي' : 'Total'}
              value={`${appointment.price} ${appointment.currency || ''}`.trim()}
            />
          )}
        </Section>

        {!isDoctorView && isCompleted && (
          <Section icon={Star} title={lang === 'ar' ? 'التقييم' : 'Feedback'}>
            {hasDoctorReview ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-bold">
                      {lang === 'ar' ? 'تم إرسال تقييمك لهذا الموعد' : 'Review already submitted for this appointment'}
                    </p>
                    {appointment.doctor_review_rating != null && (
                      <div className="mt-2 flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 ${
                              star <= Math.round(appointment.doctor_review_rating)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-emerald-300/70 dark:text-emerald-200/35'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    {appointment.doctor_review_comment && (
                      <p className="mt-2 text-sm font-semibold text-emerald-700 dark:text-emerald-100">
                        {appointment.doctor_review_comment}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Button
                className="h-14 w-full rounded-2xl bg-[#6270c8] text-lg font-bold text-white hover:bg-[#5360b5]"
                disabled={!canLeaveDoctorReview}
                onClick={() =>
                  navigate(reviewPath, {
                    state: {
                      doctor: appointment.doctor,
                      appointment,
                      from: location.pathname,
                    },
                  })
                }
              >
                <Star className="me-2 h-5 w-5" />
                {lang === 'ar' ? 'اترك تقييماً' : 'Leave a Review'}
              </Button>
            )}
          </Section>
        )}

        {appointment.notes?.trim() && (
          <Section icon={NotebookPen} title={lang === 'ar' ? 'ملاحظات' : 'Notes'}>
            <p className="text-base leading-7 text-slate-700 dark:text-slate-200">{appointment.notes}</p>
          </Section>
        )}

        {appointment.clinic?.name && (
          <Section icon={Stethoscope} title={lang === 'ar' ? 'العيادة' : 'Clinic'}>
            <DetailRow label={lang === 'ar' ? 'اسم العيادة' : 'Clinic Name'} value={appointment.clinic.name} />
          </Section>
        )}

        {(canCancel || (isDoctorView && nextStatus)) && (
          <Section icon={XCircle} title={lang === 'ar' ? 'الإجراءات' : 'Actions'}>
            <div className="space-y-4">
              {isDoctorView && nextStatus && (
                <>
                  <Button
                    className="h-14 w-full rounded-2xl bg-[#2f6fed] text-lg font-bold text-white hover:bg-[#255fd0]"
                    onClick={handleAdvanceStatus}
                    disabled={advancingStatus}
                  >
                    {advancingStatus ? (
                      <>
                        <Loader2 className="me-2 h-5 w-5 animate-spin" />
                        {lang === 'ar' ? 'جارٍ تحديث الحالة...' : 'Updating status...'}
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="me-2 h-5 w-5" />
                        {lang === 'ar' ? `التحويل إلى ${nextStatusLabel}` : `Move to ${nextStatusLabel}`}
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'هذا الزر ينقل الموعد مباشرة إلى الحالة التالية.' : 'This button moves the appointment directly to the next status.'}
                  </p>
                </>
              )}

              {canCancel && (
                <>
                  <Button
                    className="h-14 w-full rounded-2xl bg-[#ff4538] text-lg font-bold text-white hover:bg-[#ed392d]"
                    onClick={handleCancel}
                    disabled={cancelling}
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="me-2 h-5 w-5 animate-spin" />
                        {lang === 'ar' ? 'جارٍ الإلغاء...' : 'Cancelling...'}
                      </>
                    ) : (
                      <>
                        <XCircle className="me-2 h-5 w-5" />
                        {lang === 'ar' ? 'إلغاء الموعد' : 'Cancel Appointment'}
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'الإلغاء متاح فقط عندما تكون حالة الموعد قيد الانتظار أو مقبول.' : 'Cancellation is available only when the appointment is pending or accepted.'}
                  </p>
                </>
              )}
            </div>
          </Section>
        )}
      </section>
    </div>
  );
};

export default AppointmentDetailsPage;

